import { LockOutlined, PhoneOutlined } from "@ant-design/icons";
import { Button, Card, Form, Input, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useLogin } from "../hooks/useAuth";
import type { LoginRequest } from "../types";

const { Title, Text } = Typography;

export function LoginForm() {
	const { t } = useTranslation("auth");
	const [form] = Form.useForm<LoginRequest>();
	const { mutate: login, isPending } = useLogin();

	const onFinish = (values: LoginRequest) => {
		// Remove spaces from phone and add +998 prefix
		const phoneWithCountryCode = `+998${values.phone.replace(/\s+/g, "")}`;
		login({ ...values, phone: phoneWithCountryCode });
	};

	return (
		<Card className="w-full max-w-md shadow-lg">
			<div className="text-center mb-8">
				<Title level={2} className="!mb-2">
					{t("login.title")}
				</Title>
				<Text type="secondary">{t("login.subtitle")}</Text>
			</div>

			<Form
				form={form}
				name="login"
				onFinish={onFinish}
				layout="vertical"
				requiredMark={false}
				size="large"
			>
				<Form.Item
					name="phone"
					label={t("login.phoneLabel")}
					rules={[
						{ required: true, message: t("login.phoneRequired") },
						{
							pattern: /^\d{9}$/,
							message: t("login.phoneFormat"),
						},
					]}
				>
					<Input
						prefix={<PhoneOutlined className="text-gray-400" />}
						addonBefore="+998"
						placeholder="901234567"
						autoComplete="tel"
						maxLength={9}
					/>
				</Form.Item>

				<Form.Item
					name="password"
					label={t("login.passwordLabel")}
					rules={[
						{ required: true, message: t("login.passwordRequired") },
						{ min: 6, message: t("login.passwordMin") },
					]}
				>
					<Input.Password
						prefix={<LockOutlined className="text-gray-400" />}
						placeholder={t("login.passwordPlaceholder")}
						autoComplete="current-password"
					/>
				</Form.Item>

				<Form.Item className="!mb-0">
					<Button type="primary" htmlType="submit" loading={isPending} block>
						{t("login.submit")}
					</Button>
				</Form.Item>
			</Form>
		</Card>
	);
}
